import React from 'react';
import { AlertCircle, Lock } from 'lucide-react';
import { pillPrimary } from '../ui/pills';
import { statutErreur } from '../../lib/organisationApi';

/** États communs des écrans d'organisation : chargement (squelettes), erreur, droits insuffisants (L01-05). */

export const Squelettes: React.FC<{ lignes?: number }> = ({ lignes = 4 }) => (
    <div role="status" aria-live="polite" aria-label="Chargement en cours" className="space-y-3">
        {Array.from({ length: lignes }).map((_, i) => (
            <div key={i} className="h-[72px] rounded-xl bg-surface-soft border border-border animate-pulse" />
        ))}
    </div>
);

export const DroitsInsuffisants: React.FC = () => (
    <div className="bg-surface border border-border rounded-2xl px-6 py-12 text-center">
        <span className="mx-auto mb-3 w-11 h-11 rounded-full bg-surface-soft flex items-center justify-center text-content-muted"><Lock size={20} /></span>
        <div className="text-[15px] font-semibold text-content">Réservé à la direction</div>
        <p className="mt-1 text-[13px] text-content-muted max-w-md mx-auto">
            Vous n'avez pas accès à cette section. Demandez-le à la direction de votre établissement. Les modules autorisés pour vous restent dans la barre latérale ; les modules non activés ne sont pas affichés.
        </p>
    </div>
);

export const ErreurChargement: React.FC<{ titre: string; message?: string; onRetry: () => void }> = ({ titre, message, onRetry }) => (
    <div role="alert" className="bg-surface border border-border rounded-2xl px-6 py-12 text-center">
        <span className="mx-auto mb-3 w-11 h-11 rounded-full bg-state-danger-soft flex items-center justify-center text-state-danger"><AlertCircle size={20} /></span>
        <div className="text-[15px] font-semibold text-content">{titre}</div>
        <p className="mt-1 text-[13px] text-content-muted max-w-md mx-auto">{message || 'La liste n\'a pas pu être chargée. Vérifiez votre connexion puis réessayez.'}</p>
        <button type="button" onClick={onRetry} className={`${pillPrimary(36)} mt-4`}>Réessayer</button>
    </div>
);

/** Choisit l'état d'échec à afficher selon le statut HTTP. */
export const EtatEchec: React.FC<{ erreur: unknown; titre: string; onRetry: () => void }> = ({ erreur, titre, onRetry }) =>
    statutErreur(erreur) === 403 ? <DroitsInsuffisants /> : <ErreurChargement titre={titre} onRetry={onRetry} />;

/** Sous-titre « Service · Hôtel Les Tamaris » et pastilles réutilisées par les fiches. */
export const Pastille: React.FC<{ children: React.ReactNode; ton?: 'neutre' | 'warn' | 'ok' | 'info' | 'danger'; icone?: React.ReactNode }> = ({ children, ton = 'neutre', icone }) => {
    const classes = {
        neutre: 'bg-surface-soft text-content-muted border-border',
        warn: 'bg-state-warn-soft text-state-warn border-transparent',
        ok: 'bg-state-ok-soft text-state-ok border-transparent',
        info: 'bg-state-info-soft text-state-info border-transparent',
        danger: 'bg-state-danger-soft text-state-danger border-transparent',
    }[ton];
    return <span className={`inline-flex items-center gap-1 h-[22px] px-2 rounded-full border text-[11px] font-semibold whitespace-nowrap ${classes}`}>{icone}{children}</span>;
};
